import { h } from 'preact';
import classNames from 'classnames';
import ChatIcon from 'widget_main/components/ChatIcon';
import { useTranslations } from 'i18n/hooks';
import { useLayoutEffect, useState } from 'preact/hooks';
import styles from './avatar.scss';

import messages from './messages';
import {
  normalizeAvatarUrlForSessionListLoad,
  sessionListInitialsLetter,
  shouldAttemptAvatarImageLoad,
} from './avatarHelpers';

interface AvatarProps {
  size?: number;
  className?: string;
  avatarUrl?: string;

  userDisplayName?: string;
}

type ImageFetchState = 'fetching' | 'success' | 'error' | 'skipped';

const Avatar = ({
  size,
  className,
  avatarUrl,
  userDisplayName,
}: AvatarProps) => {
  const translations = useTranslations(messages);
  const normalizedAvatarUrl = normalizeAvatarUrlForSessionListLoad(
    avatarUrl ?? null,
  );
  const avatarIsLoadable = shouldAttemptAvatarImageLoad(normalizedAvatarUrl);
  const [imageFetchState, setImageFetchState] = useState<ImageFetchState>(() =>
    avatarIsLoadable ? 'fetching' : 'skipped',
  );

  // useLayoutEffect: when the row URL changes, reset before paint so we do not briefly
  // render initials/icon from the previous URL's `error` state while the new image loads.
  useLayoutEffect(() => {
    setImageFetchState(avatarIsLoadable ? 'fetching' : 'skipped');
  }, [avatarIsLoadable, normalizedAvatarUrl]);

  const resolvedSize = size ?? 40;
  const initialsFontSizePx = Math.floor((11 * resolvedSize) / 25);
  const initialsLetter = sessionListInitialsLetter(userDisplayName);

  const renderInitials = () => (
    <span
      class={styles.initials}
      style={{ fontSize: `${initialsFontSizePx}px`, lineHeight: 1 }}
    >
      {initialsLetter}
    </span>
  );

  const renderChatIconFallback = () => (
    <ChatIcon size={{ width: resolvedSize, height: resolvedSize }} />
  );

  const renderAvatarUrl = () => {
    if (avatarIsLoadable && normalizedAvatarUrl) {
      if (imageFetchState === 'error') {
        return initialsLetter ? renderInitials() : renderChatIconFallback();
      }

      const imageAlt = userDisplayName
        ? `${userDisplayName} ${translations.agentAvatar}`
        : translations.agentAvatar;

      return (
        <img
          class={classNames(styles.customImg, {
            [styles.hideImg]: imageFetchState === 'fetching',
          })}
          src={normalizedAvatarUrl}
          alt={imageAlt}
          onError={() => {
            setImageFetchState('error');
          }}
          onLoad={() => {
            setImageFetchState('success');
          }}
        />
      );
    }

    if (initialsLetter) {
      return renderInitials();
    }

    return renderChatIconFallback();
  };

  return (
    <div
      style={{ width: `${resolvedSize}px`, height: `${resolvedSize}px` }}
      class={classNames(styles.avatar, className)}
      aria-hidden={true}
    >
      {renderAvatarUrl()}
    </div>
  );
};

Avatar.defaultProps = {
  size: 40,
};
export default Avatar;
